import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import getTimeStamp from 'getTimeStamp';
import getIdentifier from 'getIdentifier';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import useSaveWidget from 'components/PerformanceBoards/hooks/useSaveWidget';
import useSlideoutDateFilterState from 'components/PerformanceBoards/hooks/useSlideoutDateFilterState';
import getSlideoutDateFilter from '../../getSlideoutDateFilter';
import GridSlideoutWizard from './GridSlideoutWizard';

const GridSlideoutWizardContainer = ({
  close,
  editingSection,
}: {
  close: () => void;
  editingSection: PerformanceBoardTypes.SlideOut.GridSection | undefined;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);

  const [id] = useState<string>(
    editingSection ? editingSection.id : getIdentifier(),
  );
  const [createdBy] = useState<string>(
    editingSection ? editingSection.createdBy : currentUserId,
  );
  const [createdOn] = useState<string>(
    editingSection ? editingSection.createdOn : getTimeStamp(),
  );
  const [title, setTitle] = useState<string>(
    editingSection ? editingSection.title : '',
  );
  const [fields, setFields] = useState<string[]>(
    editingSection ? editingSection.fields : [],
  );
  const [dataset, setDataset] = useState<string | undefined>(
    editingSection ? editingSection.dataset : undefined,
  );
  const [scope, setScope] = useState<FilterPlateType[]>(
    editingSection && editingSection.scope ? editingSection.scope : [],
  );
  const {
    dateFilter,
    onDateFilterModeChanged,
    setInterval,
    setLookback,
    setDateField,
  } = useSlideoutDateFilterState({
    initial: editingSection ? getSlideoutDateFilter(editingSection) : undefined,
  });

  // The scope and query date field belong to the dataset, so reset them when
  // it changes. (The fields are reset by DatasetFieldPicker.)
  const onDatasetChanged: React.Dispatch<
    React.SetStateAction<string | undefined>
  > = useCallback(
    (action) => {
      setDataset(action);
      setScope([]);
      setDateField(undefined);
    },
    [setDateField],
  );

  const [gridSection, setGridSection] = useState<
    PerformanceBoardTypes.SlideOut.GridSection | undefined
  >();

  const saveWidget = useSaveWidget({
    section: gridSection,
    editingSection,
  });

  const isDisabled =
    !dataset || dataset.length === 0 || fields.length === 0 || title === '';

  const onTitleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setTitle(event.target.value);
  };

  useEffect(() => {
    if (isDisabled || !dataset) {
      setGridSection(undefined);
      return;
    }

    const newGridSection: PerformanceBoardTypes.SlideOut.GridSection = {
      id: id,
      createdBy: createdBy,
      updatedBy: currentUserId,
      createdOn: createdOn,
      updatedOn: getTimeStamp(),
      sectionType: 'Simple Grid',
      title: title,
      dataset: dataset,
      fields: fields,
      dateFilter,
      scope,
    };

    setGridSection(newGridSection);
  }, [
    createdBy,
    createdOn,
    currentUserId,
    dataset,
    dateFilter,
    fields,
    id,
    isDisabled,
    scope,
    title,
  ]);

  return (
    <FilterPlatesProvider
      dataType={dataset}
      scope={scope}
      setScope={setScope}
      isValueLookupUnfiltered
    >
      <GridSlideoutWizard
        saveWidget={saveWidget}
        title={title}
        onTitleChanged={onTitleChanged}
        dateFilter={dateFilter}
        onDateFilterModeChanged={onDateFilterModeChanged}
        setInterval={setInterval}
        setLookback={setLookback}
        setDateField={setDateField}
        fields={fields}
        setFields={setFields}
        dataset={dataset}
        setDataset={onDatasetChanged}
        isDisabled={isDisabled}
        gridSection={gridSection}
        close={close}
      />
    </FilterPlatesProvider>
  );
};

export default GridSlideoutWizardContainer;
